/**
 * SYSTEM ADMIN endpoints (Gsa / User / Credential / Bank) — migration guide Bölüm 3.7 & 3.10.
 * See api/types/system.ts for the full per-entity confirmation notes (swagger + live curl).
 */
import { apiPost, apiPostMultipart, apiPostSession } from '../client';
import type { ResponseBase } from '../types/common';
import type {
  Bank,
  BankListResponse,
  Credential,
  CredentialListResponse,
  GsaAddRequest,
  GsaEditRequest,
  GsaListResponse,
  SystemUser,
  UserListResponse,
} from '../types/system';

/* --------------------------------- Gsa ---------------------------------
 * Confirmed via swagger: `/Gsa/AddGsa` (flat `GsaAddRequest` body, session merged at top
 * level like any other apiPost call).
 *
 * RE-VERIFIED 2026-08-10 against local-backend swagger (`http://localhost:1538/`):
 * `/Gsa/GetGsaList`'s request DTO is `GsaRequest { SessionHeader }` — a WRAPPER object
 * with the session nested under a `SessionHeader` key — NOT a bare `SessionHeader` body
 * like every other `Get*` route in this file. The previous `apiPostSession` wiring sent
 * the session fields flat at the top level, so the server-side `request.SessionHeader`
 * deserialized to `null` (a genuine "request null" bug, confirmed live: the bare-shape
 * body causes this route to hang/never respond, while the wrapped shape returns 200
 * with real rows instantly). Fixed by switching to `apiPost` with an explicit empty body
 * so the default `{...body, sessionHeader}` merge produces the correct wrapper shape.
 * Response field `gsaList` (from `GsaList`) was already correct.
 *
 * CONFIRMED against live swagger 2026-08-27: `Gsa/EditGsa`, `Gsa/DeleteGsa` (both take the
 * full `GsaEditRequest`), and `Gsa/GetAllGsas` (`{sessionHeader}` wrapper) all exist.
 * `Gsa/UploadLogo` does NOT — logo upload stays a web-tier file operation in the legacy
 * app; the React app sends the already-resolved filename via `EditGsa.logo` instead
 * (backend must expose a small upload helper, or the SPA proxies to a file endpoint).
 */

export function getGsaList(): Promise<GsaListResponse> {
  return apiPost<{}, GsaListResponse>('Gsa/GetGsaList', {});
}

/** `Gsa/GetAllGsas` — system-wide list of every non-deleted GSA (no GSA scoping), per the
 *  code-traced `Docs/api/05-corporate.md` (§Gsa/GetAllGsas). Request DTO is `GsaRequest`
 *  ({ SessionHeader } wrapper) — same shape as `GetGsaList`, so wired the same way (an
 *  explicit empty body → the default `{...body, sessionHeader}` merge). */
export function getAllGsas(): Promise<GsaListResponse> {
  return apiPost<{}, GsaListResponse>('Gsa/GetAllGsas', {});
}

export function addGsa(gsa: GsaAddRequest): Promise<ResponseBase> {
  return apiPost<GsaAddRequest, ResponseBase>('Gsa/AddGsa', gsa);
}

/** `Gsa/EditGsa` — CONFIRMED (swagger 2026-08-27). Full `GsaEditRequest` body. */
export function editGsa(gsa: GsaEditRequest): Promise<ResponseBase> {
  return apiPost<GsaEditRequest, ResponseBase>('Gsa/EditGsa', gsa);
}

/** `Gsa/DeleteGsa` — CONFIRMED (swagger 2026-08-27). Takes the SAME full `GsaEditRequest`
 *  object as EditGsa, not a bare id — so callers pass at least `{ id }` (ideally the row). */
export function deleteGsa(gsa: GsaEditRequest): Promise<ResponseBase> {
  return apiPost<GsaEditRequest, ResponseBase>('Gsa/DeleteGsa', gsa);
}

/** ⚠️ NOT in swagger 2026-08-27 — logo upload is a legacy web-tier file operation. This
 *  posts multipart best-effort; if the route is absent the caller falls back to
 *  `EditGsa` with a manually-entered filename. */
export function uploadGsaLogo(gsaId: number, file: File): Promise<ResponseBase> {
  const formData = new FormData();
  formData.append('gsaId', String(gsaId));
  formData.append('file', file);
  return apiPostMultipart<ResponseBase>('Gsa/UploadLogo', formData);
}

/* --------------------------------- User ---------------------------------
 * Confirmed via swagger + live curl: `UserRequest` wraps the full user under a `user` key.
 * `GetUsers` verified end-to-end (200, real rows). `GetUserById` 200s with a business-logic
 * null-ref error for this account/data — route + request shape confirmed accepted (not a
 * 404), so list rows are used directly for editing instead of relying on this route.
 */

export function getUsers(): Promise<UserListResponse> {
  return apiPostSession<UserListResponse>('User/GetUsers');
}

export function getUserById(id: number): Promise<UserListResponse> {
  return apiPost<{ user: Partial<SystemUser> }, UserListResponse>('User/GetUserById', { user: { id } });
}

/** `User/AddUser` — the worker always auto-generates a 9-char password and returns it as
 *  `UserResponse.GeneratedPassword` (`Docs/api/01-auth-identity.md` §AddUser); the legacy
 *  welcome-email path is dead server-side, so the SPA shows it after create. `userList`
 *  carries the one created row. */
export function addUser(user: Omit<SystemUser, 'id'>): Promise<UserListResponse> {
  return apiPost<{ user: Omit<SystemUser, 'id'> }, UserListResponse>('User/AddUser', { user });
}

export function editUser(user: SystemUser): Promise<ResponseBase> {
  return apiPost<{ user: SystemUser }, ResponseBase>('User/EditUser', { user });
}

export function deleteUser(id: number): Promise<ResponseBase> {
  return apiPost<{ user: Partial<SystemUser> }, ResponseBase>('User/DeleteUser', { user: { id } });
}

/* ------------------------------ Credential ------------------------------
 * Confirmed via local-backend swagger + live curl (see api/types/system.ts header) —
 * request wraps the entity under a `credential` key, response field is `credentials`.
 */

export function getCredentials(): Promise<CredentialListResponse> {
  return apiPostSession<CredentialListResponse>('Credential/GetCredentials');
}

export function getCredentialById(id: number): Promise<CredentialListResponse> {
  return apiPost<{ credential: Partial<Credential> }, CredentialListResponse>('Credential/GetCredentialById', {
    credential: { id },
  });
}

export function addCredential(credential: Omit<Credential, 'id'>): Promise<ResponseBase> {
  return apiPost<{ credential: Omit<Credential, 'id'> }, ResponseBase>('Credential/AddCredential', {
    credential,
  });
}

export function editCredential(credential: Credential): Promise<ResponseBase> {
  return apiPost<{ credential: Credential }, ResponseBase>('Credential/EditCredential', { credential });
}

export function deleteCredential(id: number): Promise<ResponseBase> {
  return apiPost<{ credential: Partial<Credential> }, ResponseBase>('Credential/DeleteCredential', {
    credential: { id },
  });
}

/* --------------------------------- Bank ---------------------------------
 * Confirmed via local-backend swagger + live curl (see api/types/system.ts header) —
 * request wraps the entity under a `bank` key, response field is `banks`.
 */

export function getBanks(): Promise<BankListResponse> {
  return apiPostSession<BankListResponse>('Bank/GetBanks');
}

export function getBankById(id: number): Promise<BankListResponse> {
  return apiPost<{ bank: Partial<Bank> }, BankListResponse>('Bank/GetBankById', { bank: { id } });
}

export function addBank(bank: Omit<Bank, 'id'>): Promise<ResponseBase> {
  return apiPost<{ bank: Omit<Bank, 'id'> }, ResponseBase>('Bank/AddBank', { bank });
}

export function editBank(bank: Bank): Promise<ResponseBase> {
  return apiPost<{ bank: Bank }, ResponseBase>('Bank/EditBank', { bank });
}

export function deleteBank(id: number): Promise<ResponseBase> {
  return apiPost<{ bank: Partial<Bank> }, ResponseBase>('Bank/DeleteBank', { bank: { id } });
}
